
import { useState, useEffect, useCallback } from 'react';
import { Job } from '@/types/job';
import { useToast } from "@/hooks/use-toast";

interface UseJobSwipeProps {
  initialJobs: Job[];
  onJobsExhausted?: () => void; // Added to fix type errors
}

interface UseJobSwipeReturn {
  jobs: Job[];
  currentJobIndex: number;
  swipedDirection: 'left' | 'right' | null;
  showJobDetails: boolean;
  likedJobs: Job[];
  isTransitioning: boolean;
  hasMoreJobs: boolean;
  currentJob: Job | null;
  handleSwipeLeft: () => void;
  handleSwipeRight: () => void;
  handleViewDetails: () => void;
  handleCloseDetails: () => void;
  handleResetJobs: () => void;
  // Adding the missing methods
  getCurrentIndex: () => number;
  getRemainingJobs: () => Job[];
  getFilteredJobs: () => Job[];
}

// Interface for localStorage stored jobs - exporting for reuse
export interface StoredJobs {
  likedJobs: Job[];
  dislikedJobs: Job[];
  viewedJobIds: number[];
}

const LOCAL_STORAGE_KEY = "dgk-jobs";

export const useJobSwipe = ({ initialJobs, onJobsExhausted }: UseJobSwipeProps): UseJobSwipeReturn => {
  // Initial state setup
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentJobIndex, setCurrentJobIndex] = useState(0);
  const [swipedDirection, setSwipedDirection] = useState<'left' | 'right' | null>(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [likedJobs, setLikedJobs] = useState<Job[]>([]);
  const [dislikedJobs, setDislikedJobs] = useState<Job[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { toast } = useToast();
  
  const currentJob = currentJobIndex < jobs.length ? jobs[currentJobIndex] : null;
  const hasMoreJobs = currentJobIndex < jobs.length;

  // Load jobs on mount - include logic to filter out already seen jobs if desired
  useEffect(() => {
    const storedJobsData = localStorage.getItem(LOCAL_STORAGE_KEY);
    let storedLikedJobs: Job[] = [];
    let storedDislikedJobs: Job[] = [];
    let viewedJobIds: number[] = [];

    if (storedJobsData) {
      try {
        const parsedData: StoredJobs = JSON.parse(storedJobsData);
        storedLikedJobs = parsedData.likedJobs || [];
        storedDislikedJobs = parsedData.dislikedJobs || [];
        viewedJobIds = parsedData.viewedJobIds || [];
        
        setLikedJobs(storedLikedJobs);
        setDislikedJobs(storedDislikedJobs);
      } catch (error) {
        console.error("Error parsing stored jobs:", error);
      }
    }

    // Option: Filter out already viewed jobs
    // const filteredJobs = initialJobs.filter(job => {
    //   const jobId = typeof job.id === 'string' ? parseInt(job.id) : job.id;
    //   return !viewedJobIds.includes(jobId);
    // });
    
    // For this implementation, we're showing all jobs
    setJobs(initialJobs);
  }, [initialJobs]);

  // Helper function to update localStorage
  const updateLocalStorage = useCallback((updatedLikedJobs: Job[], updatedDislikedJobs: Job[]) => {
    const viewedJobIds = [...updatedLikedJobs, ...updatedDislikedJobs].map(job => 
      typeof job.id === 'string' ? parseInt(job.id) : job.id
    );
    
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        likedJobs: updatedLikedJobs,
        dislikedJobs: updatedDislikedJobs,
        viewedJobIds
      }));
    } catch (error) {
      console.error("Error saving jobs to localStorage:", error);
      toast({
        title: "Storage Error",
        description: "Could not save your preferences",
        variant: "destructive",
      });
    }
  }, [toast]);

  const handleSwipeLeft = useCallback(() => {
    if (isTransitioning || !hasMoreJobs) return;
    
    setSwipedDirection('left');
    setIsTransitioning(true);
    
    setTimeout(() => {
      if (currentJob) {
        const updatedDislikedJobs = [...dislikedJobs, currentJob];
        setDislikedJobs(updatedDislikedJobs);
        updateLocalStorage(likedJobs, updatedDislikedJobs);
      }
      setCurrentJobIndex(prev => prev + 1);
      setSwipedDirection(null);
      setIsTransitioning(false);
      
      // Check if we've run out of jobs
      if (currentJobIndex + 1 >= jobs.length && onJobsExhausted) {
        onJobsExhausted();
      }
    }, 300);
    
    toast({
      title: "Job skipped",
      description: "You've passed on this opportunity",
    });
  }, [currentJob, dislikedJobs, hasMoreJobs, isTransitioning, likedJobs, toast, updateLocalStorage, jobs.length, currentJobIndex, onJobsExhausted]);

  const handleSwipeRight = useCallback(() => {
    if (isTransitioning || !hasMoreJobs) return;
    
    setSwipedDirection('right');
    setIsTransitioning(true);
    
    setTimeout(() => {
      if (currentJob) {
        // Check if job is already in liked jobs to prevent duplicates
        if (!likedJobs.some(job => job.id === currentJob.id)) {
          const updatedLikedJobs = [...likedJobs, currentJob];
          setLikedJobs(updatedLikedJobs);
          updateLocalStorage(updatedLikedJobs, dislikedJobs);
        }
      }
      setCurrentJobIndex(prev => prev + 1);
      setSwipedDirection(null);
      setIsTransitioning(false);
      
      // Check if we've run out of jobs
      if (currentJobIndex + 1 >= jobs.length && onJobsExhausted) {
        onJobsExhausted();
      }
    }, 300);
    
    toast({
      title: "Job liked!",
      description: "Added to your saved jobs",
      variant: "default",
    });
  }, [currentJob, dislikedJobs, hasMoreJobs, isTransitioning, likedJobs, toast, updateLocalStorage, jobs.length, currentJobIndex, onJobsExhausted]);

  const handleViewDetails = useCallback(() => {
    setShowJobDetails(true);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setShowJobDetails(false);
  }, []);

  const handleResetJobs = useCallback(() => {
    setCurrentJobIndex(0);
    setSwipedDirection(null);
    
    // Optionally clear stored jobs
    // setLikedJobs([]);
    // setDislikedJobs([]);
    // updateLocalStorage([], []);
    
    toast({
      title: "Jobs Reset",
      description: "Starting fresh with new opportunities",
    });
  }, [toast]);
  
  // Added missing helper methods
  const getCurrentIndex = useCallback(() => {
    return currentJobIndex;
  }, [currentJobIndex]);
  
  const getRemainingJobs = useCallback(() => {
    return jobs.slice(currentJobIndex);
  }, [jobs, currentJobIndex]);
  
  const getFilteredJobs = useCallback(() => {
    // You can add filtering logic here based on search terms, categories, etc.
    return jobs;
  }, [jobs]);

  return {
    jobs,
    currentJobIndex,
    swipedDirection,
    showJobDetails,
    likedJobs,
    isTransitioning,
    hasMoreJobs,
    currentJob,
    handleSwipeLeft,
    handleSwipeRight,
    handleViewDetails,
    handleCloseDetails,
    handleResetJobs,
    getCurrentIndex,
    getRemainingJobs,
    getFilteredJobs
  };
};
